import {setFilterForCompanies, setMultipleFilterForCompany} from "store/filter/filterActions";
import {TAppDispatch, TRootState} from "types";
import axios, {AxiosResponse, CancelTokenSource} from "axios";
import {
	addSpecialityClustersToList,
	getSpecialityClustersFetch,
	getSpecialtyClustersSuccess,
} from "store/search/searchActions";
import mapxSpecialityApi from "api/filterOptionsApi/SpecialityApi";
import {successResponse} from "helpers/map";
import {
	companySpecialityClusterFilterKeyByLogic,
	companySpecialtyClusterByLogicSelector,
} from "store/filter/companySpecialtyCLusterSelectors";
import {getCancelToken} from "api/cancelTokens";

const cancelTokens: {[key: string]: CancelTokenSource} = {};

export const getClusterSpecialities =
	(searchTerm = "", pageNumber = 1) =>
	async (dispatch: TAppDispatch) => {
		const requestKey = `GET_RECOMMENDED_SPECIALTIES_CLUSTER`;

		if (requestKey in cancelTokens) {
			cancelTokens[requestKey].cancel("Operation canceled due to new request.");
		}

		cancelTokens[requestKey] = axios.CancelToken.source();

		const config = {
			cancelToken: cancelTokens[requestKey].token,
		};

		dispatch(getSpecialityClustersFetch());

		const pagination = {
			page: pageNumber,
			per_page: 40,
		};

		try {
			let response: Nullable<AxiosResponse> = null;

			if (searchTerm !== "") {
				const filters = {
					name: searchTerm,
				};

				response = await mapxSpecialityApi.getFilteredClusterSpecialities(
					filters,
					pagination,
					config,
				);
			} else if (searchTerm === "") {
				response = await mapxSpecialityApi.getClusterSpecialities(pagination, config);
			}

			if (response && successResponse(response, 200)) {
				const results = response.data.results;

				let shouldAddMore = false;

				if (pageNumber > 1) {
					shouldAddMore = true;
				}

				dispatch(
					getSpecialtyClustersSuccess({
						results,
						shouldAddMore,
						selectedSpecialities: [],
					}),
				);

				if (response.data.pagination.pages === pageNumber) {
					return {
						stopPaginate: true,
					};
				} else {
					return {
						stopPaginate: false,
					};
				}
			}
		} catch (error) {
			const apiError = error as {data: string};

			return {error: apiError.data, stopPaginate: false};
		}
	};

export const setBulkSpecialityClusterForCompanies =
	(logic: string, ids: number[]) => (dispatch: TAppDispatch) => {
		const specialitiesFilterKey = companySpecialityClusterFilterKeyByLogic("_", logic);

		const payload = {
			[specialitiesFilterKey]: ids,
		};

		dispatch(setMultipleFilterForCompany(payload));
	};

export const setSpecialityClustersForCompany =
	(ids: number[], logic: string) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const specialities = companySpecialtyClusterByLogicSelector(state, logic);
		const alreadySelectedIds = ids?.filter((id) => specialities.includes(id));

		let nextSpecialities;
		if (alreadySelectedIds.length > 0) {
			nextSpecialities = specialities.filter(
				(speciality: number) => !ids.includes(speciality),
			);
		} else {
			nextSpecialities = [...specialities, ...ids];
		}

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const specialtyFilterKey = companySpecialityClusterFilterKeyByLogic(state, logic);

		dispatch(setFilterForCompanies({type: specialtyFilterKey, values: nextSpecialities}));
	};

export const moveSpecialtyClustersForCompanies =
	({from, to, ids}: {from: string; to: string; ids: number[]}) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const idsSet = new Set(ids);

		const state = getState();

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const fromFilterKey = companySpecialityClusterFilterKeyByLogic(state, from);

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const toFilterKey = companySpecialityClusterFilterKeyByLogic(state, to);

		const fromIds = companySpecialtyClusterByLogicSelector(state, from);

		const toIds = companySpecialtyClusterByLogicSelector(state, to);

		dispatch(
			setMultipleFilterForCompany({
				[fromFilterKey]: fromIds.filter((id: number) => !idsSet.has(id)),
				[toFilterKey]: [
					...toIds.filter((id: number) => !idsSet.has(id)), // prevent duplication
					...fromIds.filter((id: number) => idsSet.has(id)),
				],
			}),
		);
	};

export const fetchAndAddSpecialityClusters =
	(ids: number[]) =>
	async (dispatch: TAppDispatch): Promise<void> => {
		try {
			const payload = {
				filters: {
					specialty_clusters: ids,
				},
				pagination: {
					page: 1,
					per_page: 300,
				},
			};

			const response: AxiosResponse = await mapxSpecialityApi.getFilteredClusterSpecialities(
				payload.filters,
				payload.pagination,
				{
					cancelTokens: getCancelToken("fetchAndAddSpecialityClusters"),
				},
			);

			if (successResponse(response, 200)) {
				dispatch(addSpecialityClustersToList(response.data.results));
			}
		} catch (error) {
			console.error("Error fetching specialty clusters", error);
			throw error;
		}
	};
